import { createElement } from "react";
import type { CourseCertificateEvidenceModel } from "../models/courseCertificate";
import { formatDanishLongDate } from "@/util/date";
import {
  appendUnmatchedFailingFields,
  getFieldValidationItem,
} from "./fieldValidationHelpers";
import { FIELD_LABEL_DA } from "./fieldValidationMessages";
import CourseTimeInfoContent from "../views/documents/components/CourseTimeInfoContent";
import type { ValidationItem } from "../views/documents/components/DocumentValidationCard";

// Same wording as insurancePolicyValidation and as DocumentValidationCard's own
// fallback for null, so an absent field reads the same on every validation card.
const MISSING_VALUE = "Ikke angivet";

/**
 * Field keys this view renders a row for. Anything else the API rejects is
 * appended by `appendUnmatchedFailingFields`, so a backend rule the UI has no
 * row for can no longer reject a certificate invisibly.
 */
const RENDERED_FIELD_KEYS = [
  "isLegalCourseCertificate",
  "courseTitle",
  "courseProvider",
  "courseDate",
  "coursePeriod",
  "courseTime",
  "numberOfLessons",
  "signature",
  "participantName",
  "courseDescription",
] as const;

/**
 * How the lessons were counted is what the secretariat rejects most
 * certificates on, so the requirement sits behind an "i" on both rows that
 * carry it: the lesson count, which is always rendered, and "Kursets varighed",
 * which only appears when the API rejects it.
 *
 * `createElement` rather than JSX because this module is a `.ts` file.
 */
const courseTimeInfo = () => createElement(CourseTimeInfoContent);

/**
 * Én række til kursets datoer, ikke to.
 *
 * Beviset bærer kun en slutdato, når kurset løber over flere dage, så et
 * endagskursus har præcis én dato at vise - og "Kursusdato: 12. marts" ved
 * siden af "Kursusperiode: Endagskursus" siger ikke mere end den første.
 * Rækken hedder derfor "Kursusdato" for et endagskursus og "Kursusperiode",
 * når der er et interval at vise.
 */
const formatProviderAttestation = (
  certificate: CourseCertificateEvidenceModel | undefined,
): string => {
  if (certificate?.signature) return "Underskrevet";
  if (certificate?.providerLogo) return "Udbyders logo";
  return "Nej";
};

const formatCoursePeriod = (
  certificate: CourseCertificateEvidenceModel | undefined,
): string => {
  if (!certificate?.courseDate) {
    return MISSING_VALUE;
  }
  if (!certificate.courseEndDate) {
    return formatDanishLongDate(certificate.courseDate, MISSING_VALUE);
  }
  return `${formatDanishLongDate(certificate.courseDate, MISSING_VALUE)} - ${formatDanishLongDate(
    certificate.courseEndDate,
    MISSING_VALUE,
  )}`;
};

/**
 * Dommen på datorækken. `coursePeriod` vinder, når API'et har afsagt en - den
 * er den strengere af de to, og `courseDate` kan kun fejle på en dato, der slet
 * ikke findes, hvilket netop er det tilfælde, hvor `coursePeriod` udebliver.
 */
const coursePeriodValidation = (
  certificate: CourseCertificateEvidenceModel | undefined,
) => {
  const period = getFieldValidationItem(
    certificate?.fieldValidation,
    "coursePeriod",
  );
  return period.showValidationState
    ? period
    : getFieldValidationItem(certificate?.fieldValidation, "courseDate");
};

/** "3 t. 30 min." — samme form som sagsbehandlerfladen viser. */
const formatCourseDuration = (minutes: number | null | undefined): string => {
  if (minutes == null) return MISSING_VALUE;

  const wholeHours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (wholeHours === 0) return `${remainingMinutes} min.`;
  if (remainingMinutes === 0) return `${wholeHours} t.`;
  return `${wholeHours} t. ${remainingMinutes} min.`;
};

const createFallbackRow = (label: string, reason: string): ValidationItem => ({
  label,
  value: MISSING_VALUE,
  isValid: false,
  showValidationState: true,
  reason,
  info: label === FIELD_LABEL_DA.courseTime ? courseTimeInfo() : undefined,
});

export const createCourseCertificateValidationItems = (
  certificate: CourseCertificateEvidenceModel | undefined,
): ValidationItem[] => {
  const items: ValidationItem[] = [
    {
      label: "Er det et juridisk kursusbevis?",
      value: certificate?.isLegalCourseCertificate ? "Ja" : "Nej",
      ...getFieldValidationItem(
        certificate?.fieldValidation,
        "isLegalCourseCertificate",
      ),
    },
    {
      label: "Kursustitel",
      value: certificate?.courseTitle ?? MISSING_VALUE,
      ...getFieldValidationItem(certificate?.fieldValidation, "courseTitle"),
    },
    {
      label: "Kursusudbyder",
      value: certificate?.courseProvider ?? MISSING_VALUE,
      ...getFieldValidationItem(certificate?.fieldValidation, "courseProvider"),
    },
    {
      label: certificate?.courseEndDate
        ? FIELD_LABEL_DA.coursePeriod
        : FIELD_LABEL_DA.courseDate,
      value: formatCoursePeriod(certificate),
      ...coursePeriodValidation(certificate),
    },
    {
      label: FIELD_LABEL_DA.courseTime,
      value: formatCourseDuration(certificate?.courseTime),
      info: courseTimeInfo(),
      ...getFieldValidationItem(certificate?.fieldValidation, "courseTime"),
    },
    {
      label: "Antal lektioner",
      value: certificate?.numberOfLessons?.toString() ?? MISSING_VALUE,
      info: courseTimeInfo(),
      ...getFieldValidationItem(
        certificate?.fieldValidation,
        "numberOfLessons",
      ),
    },
    {
      // Kriterie 3: underskrift ELLER udbyderens logo. API'et rapporterer begge
      // under nøglen `signature`, så det er én række.
      label: "Underskrift eller udbyders logo",
      value: formatProviderAttestation(certificate),
      ...getFieldValidationItem(certificate?.fieldValidation, "signature"),
    },
    {
      label: "Deltagernavn",
      value: certificate?.participantName ?? MISSING_VALUE,
      ...getFieldValidationItem(
        certificate?.fieldValidation,
        "participantName",
      ),
    },
    {
      label: "Kursusbeskrivelse",
      value: certificate?.courseDescription ?? MISSING_VALUE,
      isWide: true,
      ...getFieldValidationItem(
        certificate?.fieldValidation,
        "courseDescription",
      ),
    },
  ];

  return appendUnmatchedFailingFields(
    items,
    certificate?.fieldValidation,
    RENDERED_FIELD_KEYS,
    createFallbackRow,
  );
};
